"use client"

import { useCallback } from "react"
import { getAccessToken } from "@/lib/http/authedFetch"

async function authHeaders(): Promise<Record<string, string> | null> {
  // Reuse the canonical single token read (web/lib/http/authedFetch.ts) so the access token is read
  // in one place. This client keeps its own contract: no token means "Not signed in" (thrown below).
  const token = await getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : null
}

// Thin client for the Career API. Attaches the Bearer token, enforces the
// { ok, error } envelope, and always sends cache: "no-store".
export function useCareerApi() {
  const request = useCallback(async <T = Record<string, unknown>>(path: string, init?: RequestInit): Promise<T> => {
    const h = await authHeaders()
    if (!h) throw new Error("Not signed in")
    const headers: Record<string, string> = { ...h }
    if (init?.body) headers["Content-Type"] = "application/json"
    const res = await fetch(path, { ...init, headers, cache: "no-store" })
    const json = await res.json().catch(() => ({}))
    if (!json?.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`)
    return json as T
  }, [])

  const get = useCallback(<T = Record<string, unknown>>(p: string) => request<T>(p), [request])
  const post = useCallback(
    <T = Record<string, unknown>>(p: string, body: unknown) => request<T>(p, { method: "POST", body: JSON.stringify(body) }),
    [request],
  )
  const patch = useCallback(
    <T = Record<string, unknown>>(p: string, body: unknown) => request<T>(p, { method: "PATCH", body: JSON.stringify(body) }),
    [request],
  )
  const del = useCallback(<T = Record<string, unknown>>(p: string) => request<T>(p, { method: "DELETE" }), [request])

  return { get, post, patch, del }
}
